import { Component, ElementRef, HostListener, Injector, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, afterNextRender, computed, inject, input, output, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { Router } from '@angular/router'
import { Projekti } from '@lemon/core-firebase/model/kirjanpito-projektit'
import { LaskunListaustietorivi, LaskunTila, TypeaheadAsiakas } from '@lemon/core-firebase/model/lasku'
import { TuettuKieli } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { Subject, of } from 'rxjs'
import { switchMap, takeUntil } from 'rxjs/operators'
import { LaskuKatseleComponentDataResolve, LaskuKatseleComponentExistingData } from '../../_angular/_resolvers/lasku.resolve'
import { KayttajaService } from '../../_angular/service/kayttaja.service'
import { LaskuService } from '../../_angular/service/lasku/lasku.service'
import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { annaAikavalinTeksti, laskeHakutulostenYhteissumma, LaskuhaunEhdot, onkoLaskuhaullaEhtoja, osuikoKlikkausAlueelle, rakennaLaskuhakupyynto, vaihdaValinta } from '../../_jaettu-angular/laskut/haku/lasku-haku-logiikka'
import { LaskutusHakuStateService } from '../../_jaettu-angular/laskut/haku/laskutus-haku-state.service'
import { ProjektitService } from '../../_jaettu-angular/projektit.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { LaskuHakuDataSourceLemonaid } from './lasku-haku-lemonaid.datasource'

interface TilaChip {
  tila: LaskunTila
  nimi: string
}

interface ValittuRajaus {
  avain: string
  nimi: string
}

type LaskuHakuPaneeli = 'aikavali' | 'tila' | 'asiakas' | 'projekti'

const HAETTAVAT_TILAT: LaskunTila[] = [
  LaskunTila.avoin,
  LaskunTila.eraantynyt,
  LaskunTila.luonnos,
  LaskunTila.maksettu,
  LaskunTila.hyvitetty,
  LaskunTila.maksettuLiikaa,
  LaskunTila.luottotappio,
  LaskunTila.mitatoity,
  LaskunTila.poistettu,
]

@Component({
  selector: 'app-lasku-haku',
  templateUrl: './lasku-haku.component.html',
  styleUrls: ['./lasku-haku.component.css'],
  standalone: false,
})
export class LaskuHakuComponent implements OnInit, OnChanges, OnDestroy {
  readonly avoinna = input(false)
  readonly suljettu = output<void>()

  // Kenttä on @if-lohkon sisällä, joten setter laukeaa vasta kun dialogi avataan ja kenttä ilmestyy DOMiin
  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      afterNextRender(() => kentta.nativeElement.focus(), { injector: this._injector })
    }
  }

  readonly hakuteksti = form(signal(''))
  readonly hakusana = computed(() => this.hakuteksti().value())
  readonly viimeiset12kkAktiivinen = signal(true)
  readonly alkuPvm = form(signal<Date | null>(null))
  readonly loppuPvm = form(signal<Date | null>(null))
  readonly valittuAlkuPvm = computed(() => this.alkuPvm().value())
  readonly valittuLoppuPvm = computed(() => this.loppuPvm().value())
  readonly aikavalinTeksti = computed(() => this._annaAikavalinTeksti())
  readonly avoinPaneeli = signal<LaskuHakuPaneeli | null>(null)

  readonly kaikkiTilat = signal<TilaChip[]>([])
  readonly valitutTilat = signal<LaskunTila[]>([])
  readonly valitutTilatLista = computed(() => this.valitutTilat().map(tila => ({ tila, nimi: this.kaikkiTilat().find(chip => chip.tila === tila)?.nimi ?? '' })))

  readonly kaikkiAsiakkaat = signal<TypeaheadAsiakas[]>([])
  readonly valitutAsiakkaat = signal<string[]>([])
  readonly asiakasRajaus = form(signal(''))
  readonly rajatutAsiakkaat = computed(() => this._rajaaAsiakkaat())
  readonly valitutAsiakkaatLista = computed(() => this._annaValitutRajaukset(this.valitutAsiakkaat(), this.kaikkiAsiakkaat()))

  readonly kaikkiProjektit = signal<Projekti[]>([])
  readonly valitutProjektit = signal<string[]>([])
  readonly valitutProjektitLista = computed(() => this._annaValitutRajaukset(this.valitutProjektit(), this.kaikkiProjektit()))
  readonly naytaSummatVerottomina = signal(false)
  readonly naytaProjektitSuodatin = signal(false)

  readonly tulokset = signal<LaskunListaustietorivi[]>([])
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  readonly yhteissummaSisAlv = computed(() => laskeHakutulostenYhteissumma(this.tulokset(), this.kokonaismaara(), false, this._currencyService))
  readonly yhteissummaAlv0 = computed(() => laskeHakutulostenYhteissumma(this.tulokset(), this.kokonaismaara(), true, this._currencyService))
  readonly sivu = signal(0)
  readonly sivunKoko = signal(50)
  readonly sivukoot = [50, 100, 200]
  readonly onkoHakuehtoja = computed(() => onkoLaskuhaullaEhtoja(this._annaEhdot()))

  private readonly _injector = inject(Injector)
  private readonly _kieli = signal<TuettuKieli | null>(null)
  private _ngUnsubscribe = new Subject<void>()

  constructor(
    private _dataSource: LaskuHakuDataSourceLemonaid,
    private _dateService: DateService,
    private _kayttajaService: KayttajaService,
    private _laskuService: LaskuService,
    private _projektitService: ProjektitService,
    private _laskuKatseleComponentDataResolve: LaskuKatseleComponentDataResolve,
    private _hakuStateService: LaskutusHakuStateService,
    private _router: Router,
    private _translationService: LemonTranslationService,
    private _currencyService: CurrencyService,
  ) {
    arvonMuutokset(this.hakuteksti).subscribe(() => this.hakutekstiMuuttui())
  }

  ngOnInit() {
    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kieli => {
      this._kieli.set(kieli)
      this.kaikkiTilat.set(HAETTAVAT_TILAT.map(tila => ({ tila: tila, nimi: this._translationService.lokalisoiKielella(`lasku.listaus.tila.${tila}`, kieli) })))
    })

    this._dataSource.dataSourceTransformed
      .connect(null)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(rivit => this.tulokset.set(rivit))
    this._dataSource.lataaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(lataa => this.haetaan.set(lataa))
    this._dataSource.kokonaismaaraObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kokonaismaara => this.kokonaismaara.set(kokonaismaara))

    const tallennettuHakutila = this._hakuStateService.lasku.onkoHakutilaTallennettu(this._router.url) ? this._hakuStateService.lasku.palautaHakutila() : null
    if (tallennettuHakutila) {
      asetaArvo(this.hakuteksti, tallennettuHakutila.hakuteksti, { ilmoita: false })
      this.viimeiset12kkAktiivinen.set(tallennettuHakutila.viimeiset12kkAktiivinen)
      asetaArvo(this.alkuPvm, tallennettuHakutila.alkuPvm ? this._dateService.numberToDate(tallennettuHakutila.alkuPvm) : null)
      asetaArvo(this.loppuPvm, tallennettuHakutila.loppuPvm ? this._dateService.numberToDate(tallennettuHakutila.loppuPvm) : null)
      this.valitutTilat.set(tallennettuHakutila.valitutTilat)
      this.valitutAsiakkaat.set(tallennettuHakutila.valitutAsiakkaat)
      this.naytaSummatVerottomina.set(tallennettuHakutila.naytaSummatVerottomina)
      this.valitutProjektit.set(tallennettuHakutila.valitutProjektit)
      this.sivu.set(tallennettuHakutila.sivu)
      this.sivunKoko.set(tallennettuHakutila.sivunKoko)
      this._hae()
    }

    this._kayttajaService.kayttajanTiedotObservable
      .pipe(
        switchMap(tiedot => (tiedot ? this._laskuService.getTypeaheadAsiakkaat(tiedot) : of<TypeaheadAsiakas[]>([]))),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(asiakkaat => {
        this.kaikkiAsiakkaat.set([...(asiakkaat ?? [])].sort((a, b) => (a.nimi ?? '').localeCompare(b.nimi ?? '', 'fi')))
      })

    this._projektitService.naytaProjektitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(nayta => {
      this.naytaProjektitSuodatin.set(nayta)
      if (!nayta && this.valitutProjektit().length > 0) {
        this.valitutProjektit.set([])
        if (this.avoinPaneeli() === 'projekti') {
          this.avoinPaneeli.set(null)
        }
        this._haeAlusta()
      }
    })

    this._projektitService.naytettavatKirjanpitoprojektitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(projektit => {
      this.kaikkiProjektit.set(projektit ?? [])
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this._hae()
    } else {
      this._dataSource.hae(null)
      this.avoinPaneeli.set(null)
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (!this.avoinna()) {
      return
    }
    if (this.avoinPaneeli() !== null) {
      this.avoinPaneeli.set(null)
    } else {
      this.sulje()
    }
  }

  @HostListener('document:click', ['$event'])
  dokumenttiaKlikattu(event: MouseEvent) {
    if (!this.avoinna() || this.avoinPaneeli() === null) {
      return
    }
    if (!osuikoKlikkausAlueelle(event, ['dropdown-wrap', 'cdk-overlay-container'])) {
      this.avoinPaneeli.set(null)
    }
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this._haeAlusta()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu.set(event.pageIndex)
    this.sivunKoko.set(event.pageSize)
    this._hae()
  }

  vaihdaPaneeli(paneeli: LaskuHakuPaneeli) {
    this.avoinPaneeli.update(avoin => (avoin === paneeli ? null : paneeli))
  }

  naytaVerottomina(verottomina: boolean) {
    this.naytaSummatVerottomina.set(verottomina)
  }

  toggleViimeiset12kk() {
    this.viimeiset12kkAktiivinen.update(aktiivinen => !aktiivinen)
    if (this.viimeiset12kkAktiivinen()) {
      asetaArvo(this.alkuPvm, null)
      asetaArvo(this.loppuPvm, null)
    }
    this._haeAlusta()
  }

  valitseAikavali() {
    if (!this.alkuPvm().value() && !this.loppuPvm().value()) {
      return
    }
    this.avoinPaneeli.set(null)
  }

  poistaAikavali() {
    this.viimeiset12kkAktiivinen.set(false)
    asetaArvo(this.alkuPvm, null)
    asetaArvo(this.loppuPvm, null)
    this._haeAlusta()
  }

  alkuPvmMuuttui() {
    if (this.alkuPvm().value()) {
      this.viimeiset12kkAktiivinen.set(false)
    }
    this._haeAlusta()
  }

  loppuPvmMuuttui() {
    if (this.loppuPvm().value()) {
      this.viimeiset12kkAktiivinen.set(false)
    }
    this._haeAlusta()
  }

  onkoTilaValittu(tila: LaskunTila): boolean {
    return this.valitutTilat().includes(tila)
  }

  vaihdaTila(tila: LaskunTila) {
    this.valitutTilat.update(valitut => vaihdaValinta(valitut, tila))
    this._haeAlusta()
  }

  poistaTilaValinta(tila: LaskunTila) {
    this.valitutTilat.update(valitut => valitut.filter(valittu => valittu !== tila))
    this._haeAlusta()
  }

  tyhjennaTilat() {
    if (this.valitutTilat().length === 0) {
      return
    }
    this.valitutTilat.set([])
    this._haeAlusta()
  }

  onkoAsiakasValittu(avain: string): boolean {
    return this.valitutAsiakkaat().includes(avain)
  }

  vaihdaAsiakas(avain: string) {
    this.valitutAsiakkaat.update(valitut => vaihdaValinta(valitut, avain))
    this._haeAlusta()
  }

  poistaAsiakasValinta(avain: string) {
    this.valitutAsiakkaat.update(valitut => valitut.filter(valittu => valittu !== avain))
    this._haeAlusta()
  }

  tyhjennaAsiakkaat() {
    if (this.valitutAsiakkaat().length === 0) {
      return
    }
    this.valitutAsiakkaat.set([])
    this._haeAlusta()
  }

  onkoProjektiValittu(avain: string): boolean {
    return this.valitutProjektit().includes(avain)
  }

  vaihdaProjekti(avain: string) {
    this.valitutProjektit.update(valitut => vaihdaValinta(valitut, avain))
    this._haeAlusta()
  }

  poistaProjektiValinta(avain: string) {
    this.valitutProjektit.update(valitut => valitut.filter(valittu => valittu !== avain))
    this._haeAlusta()
  }

  tyhjennaProjektit() {
    if (this.valitutProjektit().length === 0) {
      return
    }
    this.valitutProjektit.set([])
    this._haeAlusta()
  }

  avaaLasku(rivi: LaskunListaustietorivi) {
    const ehdot = this._annaEhdot()
    this._hakuStateService.lasku.tallennaHakutila({
      hakuteksti: ehdot.hakuteksti,
      viimeiset12kkAktiivinen: ehdot.viimeiset12kk,
      alkuPvm: ehdot.alkuPvm,
      loppuPvm: ehdot.loppuPvm,
      valitutTilat: ehdot.tilat,
      valitutAsiakkaat: ehdot.asiakasAvaimet,
      naytaSummatVerottomina: this.naytaSummatVerottomina(),
      valitutProjektit: ehdot.projektiAvaimet,
      sivu: ehdot.sivu,
      sivunKoko: ehdot.sivunKoko,
      paluureitti: this._router.url,
    })
    const existing: LaskuKatseleComponentExistingData = {
      juurilasku: rivi.juurilasku,
      kasiteltava: rivi.kasiteltava,
    }
    this._laskuKatseleComponentDataResolve.asetaOlemassaolevaData(existing)
    this._router.navigate(['/laskutus/laskut/', rivi.juuriAvain, rivi.avain, 'katsele'])
  }

  private _haeAlusta() {
    this.sivu.set(0)
    this._hae()
  }

  private _hae() {
    this._dataSource.hae(rakennaLaskuhakupyynto(this._annaEhdot(), this._dateService.currentNumberDate(), this._dateService))
  }

  private _annaEhdot(): LaskuhaunEhdot {
    const alkuPvm = this.alkuPvm().value()
    const loppuPvm = this.loppuPvm().value()
    return {
      hakuteksti: this.hakuteksti().value(),
      viimeiset12kk: this.viimeiset12kkAktiivinen(),
      alkuPvm: alkuPvm ? this._dateService.dateToNumber(alkuPvm) : null,
      loppuPvm: loppuPvm ? this._dateService.dateToNumber(loppuPvm) : null,
      tilat: this.valitutTilat(),
      asiakasAvaimet: this.valitutAsiakkaat(),
      projektiAvaimet: this.valitutProjektit(),
      sivu: this.sivu(),
      sivunKoko: this.sivunKoko(),
    }
  }

  private _annaAikavalinTeksti(): string {
    const kieli = this._kieli()
    if (!kieli) {
      return ''
    }
    const alku = this.alkuPvm().value()
    const loppu = this.loppuPvm().value()
    return annaAikavalinTeksti(alku ? this._dateService.muotoilePaivaDate(alku, kieli) : null, loppu ? this._dateService.muotoilePaivaDate(loppu, kieli) : null, {
      alkaen: this._translationService.lokalisoiKielella('laskut.haku.alkaen', kieli),
      paattyen: this._translationService.lokalisoiKielella('laskut.haku.paattyen', kieli),
    })
  }

  private _rajaaAsiakkaat(): TypeaheadAsiakas[] {
    const rajaus = this.asiakasRajaus().value().trim().toLowerCase()
    if (!rajaus) {
      return this.kaikkiAsiakkaat()
    }
    return this.kaikkiAsiakkaat().filter(asiakas => (asiakas.nimi ?? '').toLowerCase().includes(rajaus) || (asiakas.ytunnus ?? '').toLowerCase().includes(rajaus))
  }

  private _annaValitutRajaukset(avaimet: string[], vaihtoehdot: { avain: string; nimi: string }[]): ValittuRajaus[] {
    return avaimet.map(avain => ({ avain, nimi: vaihtoehdot.find(vaihtoehto => vaihtoehto.avain === avain)?.nimi ?? '' }))
  }
}
